'use client'

import React, { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle, XCircle, ChevronRight, Award, BookOpen } from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ─── University design tokens (dark navy / gold) ──────────────────────────────
const U = {
    bg: '#0d0d1a',
    card: 'rgba(255,255,255,0.05)',
    cardSolid: '#13132a',
    border: 'rgba(255,255,255,0.08)',
    gold: '#d4a843',
    goldDark: '#b8912e',
    blue: '#0ea5e9',
    indigo: '#0ea5e9',
    navy: '#0d0d1a',
    tx: 'rgba(255,255,255,0.88)',
    muted: 'rgba(255,255,255,0.45)',
    white: '#ffffff',
    success: '#4ade80',
    successDark: '#166534',
    error: '#f87171',
    errorDark: '#991b1b',
    font: "'Inter', system-ui, sans-serif",
}

// ─── Types ────────────────────────────────────────────────────────────────────
export interface QuizQuestion {
    id: string
    question: string
    options: string[]
    correct_index: number
    explanation: string
    concept: string
    difficulty: 'standard' | 'stretch'
}

export interface EduQuiz {
    quiz_id?: string
    questions: QuizQuestion[]
    lesson_title: string
    lesson_unit: number
    lesson_number: number
}

interface QuizInterfaceProps {
    quiz: EduQuiz
    isOpen: boolean
    onSubmit: (answers: Record<string, number>) => Promise<void>
    onDismiss: () => void
    degreeName?: string
}

type Phase = 'answering' | 'reviewing' | 'complete'

interface AnswerState {
    [questionId: string]: number
}

// ─── Option button ────────────────────────────────────────────────────────────

function OptionButton({
    index, text, selected, revealed, correct, onClick,
}: {
    index: number; text: string; selected: boolean
    revealed: boolean; correct: boolean; onClick: () => void
}) {
    const label = ['A', 'B', 'C', 'D'][index] ?? String(index + 1)

    const bg = !revealed
        ? selected ? U.indigo : U.card
        : correct ? 'rgba(74,222,128,0.12)'
            : selected ? 'rgba(248,113,113,0.12)' : U.card

    const border = !revealed
        ? selected ? U.indigo : U.border
        : correct ? U.success
            : selected ? U.error : U.border

    const textColor = !revealed
        ? selected ? U.white : U.tx
        : correct ? U.success
            : selected ? U.error : U.muted

    return (
        <motion.button
            onClick={revealed ? undefined : onClick}
            style={{
                width: '100%', textAlign: 'left',
                cursor: revealed ? 'default' : 'pointer',
                padding: '14px 16px', borderRadius: '14px',
                border: `2px solid ${border}`,
                background: bg, color: textColor,
                fontFamily: U.font, fontSize: '15px', lineHeight: 1.5,
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                transition: 'border-color 0.15s, background 0.15s',
            }}
            whileHover={revealed ? {} : { scale: 1.01, y: -1 }}
            whileTap={revealed ? {} : { scale: 0.99 }}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.06 }}
        >
            <div style={{
                width: '26px', height: '26px', borderRadius: '8px', flexShrink: 0,
                background: !revealed
                    ? selected ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)'
                    : correct ? 'rgba(74,222,128,0.2)' : selected ? 'rgba(248,113,113,0.2)' : 'rgba(255,255,255,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 800, fontSize: '12px',
                color: !revealed
                    ? selected ? U.white : U.muted
                    : correct ? U.success : selected ? U.error : U.muted,
                marginTop: '1px',
            }}>
                {revealed && correct
                    ? <CheckCircle size={14} />
                    : revealed && selected && !correct
                        ? <XCircle size={14} />
                        : label}
            </div>
            <span style={{ flex: 1, paddingTop: '2px' }}>{text}</span>
        </motion.button>
    )
}

// ─── Score ring ───────────────────────────────────────────────────────────────

function ScoreRing({ score, total }: { score: number; total: number }) {
    const pct = Math.round((score / total) * 100)
    const r = 52
    const circ = 2 * Math.PI * r
    const offset = circ - (pct / 100) * circ
    const color = pct >= 80 ? U.success : pct >= 60 ? U.gold : U.blue

    return (
        <svg width="128" height="128" style={{ margin: '0 auto', display: 'block' }}>
            <circle cx="64" cy="64" r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="8" />
            <motion.circle
                cx="64" cy="64" r={r}
                fill="none" stroke={color} strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={circ}
                strokeDashoffset={offset}
                transform="rotate(-90 64 64)"
                initial={{ strokeDashoffset: circ }}
                animate={{ strokeDashoffset: offset }}
                transition={{ duration: 1.2, ease: 'easeOut', delay: 0.3 }}
            />
            <text x="64" y="60" textAnchor="middle" fill={U.white} fontSize="28" fontWeight="800" fontFamily="system-ui">
                {score}
            </text>
            <text x="64" y="78" textAnchor="middle" fill={U.muted} fontSize="13" fontFamily="system-ui">
                / {total}
            </text>
        </svg>
    )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function QuizInterface({
    quiz, isOpen, onSubmit, onDismiss, degreeName = 'Quiz',
}: QuizInterfaceProps) {
    const { t } = useTranslation()
    const [phase, setPhase] = useState<Phase>('answering')
    const [currentQ, setCurrentQ] = useState(0)
    const [answers, setAnswers] = useState<AnswerState>({})
    const [revealed, setRevealed] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    useEffect(() => {
        if (isOpen) {
            setPhase('answering')
            setCurrentQ(0)
            setAnswers({})
            setRevealed(false)
        }
    }, [isOpen, quiz?.quiz_id])

    const questions = quiz?.questions || []
    const question: QuizQuestion | undefined = questions[currentQ]
    const totalQ = questions.length
    const selectedAnswer = question ? answers[question.id] : undefined
    const hasAnswered = selectedAnswer !== undefined

    const handleSelect = useCallback((optionIdx: number) => {
        if (revealed || !question) return
        setAnswers(a => ({ ...a, [question.id]: optionIdx }))
    }, [revealed, question])

    const handleReveal = () => setRevealed(true)

    const handleNext = () => {
        if (currentQ < totalQ - 1) {
            setCurrentQ(q => q + 1)
            setRevealed(false)
        } else {
            setPhase('reviewing')
        }
    }

    const handleSubmitAll = async () => {
        setIsSubmitting(true)
        setPhase('complete')
        try {
            await onSubmit(answers)
        } catch { /* non-fatal */ } finally {
            setIsSubmitting(false)
        }
    }

    const correctCount = questions.reduce((n, q) => {
        return answers[q.id] === q.correct_index ? n + 1 : n
    }, 0)
    const scorePct = totalQ > 0 ? Math.round((correctCount / totalQ) * 100) : 0

    if (!isOpen) return null

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    style={{
                        position: 'fixed', inset: 0, zIndex: 100,
                        display: 'flex', flexDirection: 'column',
                        background: 'rgba(0,0,0,0.65)',
                        backdropFilter: 'blur(8px)',
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <motion.div
                        style={{
                            margin: 'auto',
                            width: '100%', maxWidth: '560px',
                            maxHeight: '90dvh',
                            background: U.cardSolid,
                            borderRadius: '24px',
                            border: `1px solid ${U.border}`,
                            boxShadow: '0 32px 100px rgba(0,0,0,0.5)',
                            display: 'flex', flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                        initial={{ scale: 0.92, y: 32 }}
                        animate={{ scale: 1, y: 0 }}
                        exit={{ scale: 0.94, y: 24, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 340, damping: 30 }}
                    >
                        {/* Header bar */}
                        <div style={{
                            background: `linear-gradient(135deg, ${U.navy} 0%, #1a1a3a 100%)`,
                            padding: '18px 24px',
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            borderBottom: `1px solid ${U.border}`,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <BookOpen size={18} style={{ color: U.gold }} />
                                <span style={{ fontFamily: U.font, fontSize: '16px', fontWeight: 700, color: U.white }}>
                                    {phase === 'complete'
                                        ? t('university.quiz.resultsTitle', 'Quiz Results')
                                        : t('university.quiz.title', '{{name}} Quiz', { name: degreeName })}
                                </span>
                            </div>

                            {phase === 'answering' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                    {questions.map((_, i) => (
                                        <motion.div
                                            key={i}
                                            style={{
                                                width: i === currentQ ? '18px' : '6px',
                                                height: '6px', borderRadius: '3px',
                                                background: i < currentQ
                                                    ? U.gold
                                                    : i === currentQ ? U.white : 'rgba(255,255,255,0.2)',
                                                transition: 'all 0.25s',
                                            }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Body (scrollable) */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>

                            {/* ANSWERING PHASE */}
                            {phase === 'answering' && (
                                !question ? (
                                    <div style={{ textAlign: 'center', padding: '40px 20px', background: U.card, borderRadius: '20px' }}>
                                        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }}>
                                            <p style={{ color: U.tx, fontWeight: 700, fontSize: '18px', marginBottom: '8px' }}>
                                                {t('university.quiz.noQuestions', 'No questions found.')}
                                            </p>
                                            <p style={{ color: U.muted, fontSize: '14px', marginBottom: '24px' }}>
                                                {t('university.quiz.noQuestionsHint', 'Something went wrong generating this quiz. Please try again.')}
                                            </p>
                                            <button onClick={onDismiss} style={{
                                                padding: '12px 24px', background: U.blue, color: U.white,
                                                border: 'none', borderRadius: '12px', fontWeight: 600, cursor: 'pointer',
                                            }}>
                                                {t('university.quiz.goBack', 'Go back to Lesson')}
                                            </button>
                                        </motion.div>
                                    </div>
                                ) : (
                                    <AnimatePresence mode="wait">
                                        <motion.div
                                            key={currentQ}
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.18 }}
                                        >
                                            <div style={{ fontSize: '12px', color: U.gold, fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                {t('university.quiz.questionOf', 'Question {{n}} of {{total}}', { n: currentQ + 1, total: totalQ })}
                                                {question.difficulty === 'stretch' && (
                                                    <span style={{
                                                        marginLeft: '8px', background: U.gold, color: U.navy,
                                                        padding: '1px 7px', borderRadius: '6px', fontSize: '10px', fontWeight: 800,
                                                    }}>
                                                        ⭐ {t('university.quiz.stretch', 'Stretch')}
                                                    </span>
                                                )}
                                            </div>

                                            <p style={{
                                                fontFamily: U.font, fontSize: '17px', fontWeight: 600,
                                                color: U.tx, lineHeight: 1.6, marginBottom: '20px',
                                            }}>
                                                {question.question}
                                            </p>

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                {Array.isArray(question?.options) ? question.options.map((opt, i) => (
                                                    <OptionButton
                                                        key={i} index={i} text={opt}
                                                        selected={selectedAnswer === i}
                                                        revealed={revealed}
                                                        correct={i === question.correct_index}
                                                        onClick={() => handleSelect(i)}
                                                    />
                                                )) : (
                                                    <div style={{ padding: '20px', textAlign: 'center', background: U.card, borderRadius: '12px' }}>
                                                        <p style={{ color: U.muted }}>{t('university.quiz.loadingOptions', 'Loading question options...')}</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Explanation after reveal */}
                                            <AnimatePresence>
                                                {revealed && (
                                                    <motion.div
                                                        style={{
                                                            marginTop: '16px', padding: '14px 16px',
                                                            borderRadius: '12px', background: 'rgba(74,222,128,0.08)',
                                                            border: `1px solid rgba(74,222,128,0.2)`,
                                                        }}
                                                        initial={{ opacity: 0, y: 8 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                    >
                                                        <div style={{ fontWeight: 700, fontSize: '12px', color: U.success, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                                                            💡 {t('university.quiz.explanation', 'Explanation')}
                                                        </div>
                                                        <p style={{ fontSize: '14px', color: U.tx, lineHeight: 1.6, margin: 0 }}>
                                                            {question.explanation}
                                                        </p>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </motion.div>
                                    </AnimatePresence>
                                )
                            )}

                            {/* REVIEWING PHASE */}
                            {phase === 'reviewing' && (
                                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                    <h3 style={{ fontFamily: U.font, fontSize: '18px', fontWeight: 700, color: U.tx, marginBottom: '16px' }}>
                                        {t('university.quiz.reviewTitle', 'Review Your Answers')}
                                    </h3>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {questions.map((q, qi) => {
                                            const userAns = answers[q.id]
                                            const isCorrect = userAns === q.correct_index
                                            return (
                                                <div key={q.id} style={{
                                                    padding: '14px 16px', borderRadius: '14px',
                                                    border: `2px solid ${isCorrect ? 'rgba(74,222,128,0.4)' : 'rgba(248,113,113,0.4)'}`,
                                                    background: isCorrect ? 'rgba(74,222,128,0.06)' : 'rgba(248,113,113,0.06)',
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                                                        {isCorrect
                                                            ? <CheckCircle size={16} style={{ color: U.success, flexShrink: 0, marginTop: '2px' }} />
                                                            : <XCircle size={16} style={{ color: U.error, flexShrink: 0, marginTop: '2px' }} />}
                                                        <p style={{ fontSize: '14px', fontWeight: 600, color: U.tx, margin: 0, lineHeight: 1.5 }}>
                                                            {qi + 1}. {q.question}
                                                        </p>
                                                    </div>
                                                    {!isCorrect && (
                                                        <p style={{ fontSize: '13px', color: U.success, margin: '0 0 0 24px' }}>
                                                            ✓ {q.options[q.correct_index]}
                                                        </p>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </motion.div>
                            )}

                            {/* COMPLETE PHASE */}
                            {phase === 'complete' && (
                                <motion.div
                                    style={{ textAlign: 'center', padding: '16px 0' }}
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                >
                                    <ScoreRing score={correctCount} total={totalQ} />

                                    <motion.div
                                        initial={{ opacity: 0, y: 12 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.5 }}
                                    >
                                        <h3 style={{ fontFamily: U.font, fontSize: '22px', fontWeight: 700, color: U.tx, margin: '20px 0 8px' }}>
                                            {scorePct >= 90
                                                ? t('university.quiz.excellent', '🌟 Excellent!')
                                                : scorePct >= 70
                                                    ? t('university.quiz.wellDone', '🎉 Well Done!')
                                                    : t('university.quiz.keepGoing', '💪 Keep Going!')}
                                        </h3>
                                        <p style={{ fontSize: '15px', color: U.muted, lineHeight: 1.6, marginBottom: '8px' }}>
                                            {scorePct >= 90
                                                ? t('university.quiz.excellentMsg', "You've mastered this lesson. Outstanding work!")
                                                : scorePct >= 70
                                                    ? t('university.quiz.wellDoneMsg', "Great effort. Review any missed questions and keep building.")
                                                    : t('university.quiz.keepGoingMsg', "Don't worry — each attempt builds your understanding. Let's review and continue.")}
                                        </p>
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                                            background: 'rgba(212,168,67,0.1)', borderRadius: '10px', padding: '6px 14px',
                                            border: `1px solid rgba(212,168,67,0.25)`,
                                        }}>
                                            <Award size={14} style={{ color: U.gold }} />
                                            <span style={{ fontSize: '13px', fontWeight: 700, color: U.gold }}>
                                                {scorePct}% {t('university.quiz.correct', 'correct')}
                                            </span>
                                        </div>
                                    </motion.div>
                                </motion.div>
                            )}
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: '16px 24px',
                            borderTop: `1px solid ${U.border}`,
                            display: 'flex', gap: '10px',
                        }}>
                            {phase === 'answering' && (
                                <>
                                    {!revealed ? (
                                        <button
                                            onClick={handleReveal}
                                            disabled={!hasAnswered}
                                            style={{
                                                flex: 1, padding: '13px', borderRadius: '13px',
                                                border: 'none',
                                                background: hasAnswered ? U.blue : 'rgba(255,255,255,0.06)',
                                                color: hasAnswered ? U.white : U.muted,
                                                fontFamily: U.font, fontSize: '15px', fontWeight: 700,
                                                cursor: hasAnswered ? 'pointer' : 'not-allowed',
                                                transition: 'all 0.15s',
                                            }}
                                        >
                                            {t('university.quiz.checkAnswer', 'Check Answer')}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={handleNext}
                                            style={{
                                                flex: 1, padding: '13px', borderRadius: '13px',
                                                border: 'none',
                                                background: `linear-gradient(135deg, ${U.gold}, ${U.goldDark})`,
                                                color: U.navy,
                                                fontFamily: U.font, fontSize: '15px', fontWeight: 700,
                                                cursor: 'pointer',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                            }}
                                        >
                                            {currentQ < totalQ - 1
                                                ? t('university.quiz.nextQuestion', 'Next Question')
                                                : t('university.quiz.reviewAnswers', 'Review Answers')}
                                            <ChevronRight size={16} />
                                        </button>
                                    )}
                                </>
                            )}

                            {phase === 'reviewing' && (
                                <button
                                    onClick={handleSubmitAll}
                                    disabled={isSubmitting}
                                    style={{
                                        flex: 1, padding: '13px', borderRadius: '13px',
                                        border: 'none',
                                        background: `linear-gradient(135deg, ${U.gold}, ${U.goldDark})`,
                                        color: U.navy,
                                        fontFamily: U.font, fontSize: '15px', fontWeight: 700,
                                        cursor: 'pointer', opacity: isSubmitting ? 0.7 : 1,
                                    }}
                                >
                                    {isSubmitting
                                        ? t('university.quiz.submitting', 'Saving…')
                                        : t('university.quiz.submitQuiz', 'Submit Quiz')}
                                </button>
                            )}

                            {phase === 'complete' && (
                                <button
                                    onClick={onDismiss}
                                    style={{
                                        flex: 1, padding: '13px', borderRadius: '13px',
                                        border: 'none',
                                        background: `linear-gradient(135deg, ${U.gold}, ${U.goldDark})`,
                                        color: U.navy,
                                        fontFamily: U.font, fontSize: '15px', fontWeight: 700,
                                        cursor: 'pointer',
                                        boxShadow: '0 4px 16px rgba(212,168,67,0.3)',
                                    }}
                                >
                                    {t('university.quiz.continueLearning', 'Continue Learning')}
                                </button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}
